import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Avatar,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import ScienceIcon from '@mui/icons-material/Science';

interface Profile {
  date: string;
  description: string;
}

interface ProfessorInfo {
  name: string;
  position: string;
  email: string;
  office: string;
  education: string[];
  research: string[];
  profiles: Profile[];
}

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 300,
  height: 400,
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(2)
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  color: theme.palette.text.secondary
}));

const Professor: React.FC = () => {
  const professorInfo: ProfessorInfo = {
    name: "홍민 교수",
    position: "교수 / 소프트웨어융합대학 학장",
    email: "mhong@sch.ac.kr",
    office: "충남 아산시 신창면 순천향로22, 순천향대학교 멀티미디어관 M509호",
    profiles: [
      { date: "2024-present", description: "Dean, College of SW Convergence, Soonchunhyang University" },
      { date: "2024-present", description: "Head, AI·SW Education Center, Soonchunhyang University" },
      { date: "2019-2020", description: "Dean, Division of Career Development, Soonchunhyang University" },
      { date: "2014-2018", description: "Department Chair, Computer Software Engineering, Soonchunhyang University" },
      { date: "2013", description: "Visiting Scholar, Computer Graphics Lab, University of Colorado Denver" },
      { date: "2011-2013", description: "Assistant Dean of Academic Affairs, Soonchunhyang University" },
      { date: "2010-2011", description: "Department Chair, Computer Software Engineering, Soonchunhyang University" },
      { date: "2005-2006", description: "Post doc. / Part-time Lecturer, University of Colorado at Denver and Health Science center" },
      { date: "2002-2005", description: "Research Assistant, University of Colorado at Denver and Health Science center" },
      { date: "2001", description: "Teaching Assistant, University of Colorado at Denver and Health Science center" },
      { date: "1995-1997", description: "Communication Officer(Korean ROTC #33), Korean Army" }
    ],
    education: [
      "Ph.D., University of Colorado at Denver and Health Sciences Center (2005)",
      "M.S., University of Colorado at Boulder (2001)",
      "B.S., Soonchunhyang University (1995)"
    ],
    research: [
      "Computer Graphics",
      "Physically-based Simulation",
      "Machine Learning",
      "Computer Vision",
      "Artificial Intelligence",
      "Bio-Informatics"
    ]
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: 4,
        px: { xs: 2, sm: 4, md: 6 },
        maxWidth: '1600px !important'
      }}
    >      
      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <StyledAvatar
              alt="교수 프로필"
              src="/resources/prof.png"
              variant="rounded"
            />
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {professorInfo.name}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  {professorInfo.position}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <IconWrapper>
                  <EmailIcon />
                  <Typography>{professorInfo.email}</Typography>
                </IconWrapper>
                <IconWrapper>
                  <LocationOnIcon />
                  <Typography align="left">{professorInfo.office}</Typography>
                </IconWrapper>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SchoolIcon sx={{ mr: 1 }} />
              <Typography variant="h5">Educations</Typography>
            </Box>
            <List>
              {professorInfo.education.map((edu, index) => (
                <ListItem key={index}>
                  <ListItemText primary={edu} />
                </ListItem>
              ))}
            </List>
          </Paper>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ScienceIcon sx={{ mr: 1 }} />
              <Typography variant="h5">Research Interests</Typography>
            </Box>
            <List>
              {professorInfo.research.map((area, index) => (
                <ListItem key={index}>
                  <ListItemText primary={area} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ 
            p: 3,
            height: '100%',
            overflowY: 'auto'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SchoolIcon sx={{ mr: 1 }} />
              <Typography variant="h5">Profiles</Typography>
            </Box>
            <List>
              {professorInfo.profiles.map((profile, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={profile.date} 
                    secondary={profile.description}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: 'bold',
                        color: 'primary.main'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Professor;
